import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { Sparkles, Loader2, ArrowRight, Check, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const prefill = (() => {
    try {
      const raw = sessionStorage.getItem("klena_trial_prefill");
      if (raw) {
        sessionStorage.removeItem("klena_trial_prefill");
        return JSON.parse(raw);
      }
    } catch { /* ignore */ }
    return null;
  })();
  const [form, setForm] = useState({
    motel_name: prefill?.motel_name || "",
    location: prefill?.location || "Hamilton, New Zealand",
    owner_name: "",
    email: "",
    password: "",
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!acceptTerms) {
      setError("Please accept the Terms & Conditions to continue.");
      return;
    }
    setLoading(true);
    try {
      await signup({ ...form, accept_terms: true });
      toast.success("Welcome to KlenaPro — your 14-day free trial has started!");
      navigate("/manager");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 py-12 sm:px-16 lg:px-20">
        <div className="mx-auto w-full max-w-md k-fade-in">
          <div className="mb-8 flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary">
              <Sparkles className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <div className="font-display text-2xl font-extrabold leading-none">KlenaPro</div>
              <div className="label-caps text-muted-foreground">Start free trial</div>
            </div>
          </div>

          <h1 className="font-display text-3xl font-extrabold sm:text-4xl">Create your motel account</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            14 days free. No card required. $1/cleaner + $1/room per week, billed monthly after trial.
          </p>

          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="signup-form">
            <div className="space-y-1.5">
              <Label>Motel name</Label>
              <Input data-testid="signup-motel-input" value={form.motel_name} onChange={set("motel_name")} placeholder="Quantum Lodge Motor Inn" required />
            </div>
            <div className="space-y-1.5">
              <Label>Location</Label>
              <Input data-testid="signup-location-input" value={form.location} onChange={set("location")} required />
            </div>
            <div className="space-y-1.5">
              <Label>Your name</Label>
              <Input data-testid="signup-name-input" value={form.owner_name} onChange={set("owner_name")} placeholder="Owner / Manager name" required />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input data-testid="signup-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@motel.co.nz" required />
              </div>
              <div className="space-y-1.5">
                <Label>Password</Label>
                <div className="relative">
                  <Input data-testid="signup-password-input" type={showPw ? "text" : "password"} value={form.password} onChange={set("password")} placeholder="••••••••" required className="pr-10" />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    data-testid="signup-password-toggle"
                    aria-label={showPw ? "Hide password" : "Show password"}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
            {error && <p data-testid="signup-error" className="text-sm font-medium text-destructive">{error}</p>}
            <label className="flex items-start gap-2.5 text-sm" data-testid="signup-terms-label">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                data-testid="signup-terms-checkbox"
                className="mt-0.5 h-4 w-4 accent-primary"
              />
              <span className="text-muted-foreground">
                I agree to the{" "}
                <Link to="/terms" target="_blank" className="font-semibold text-foreground underline underline-offset-4" data-testid="signup-terms-link">
                  Terms &amp; Conditions
                </Link>
                .
              </span>
            </label>
            <Button type="submit" data-testid="signup-submit-button" disabled={loading} className="k-press h-11 w-full gap-2 text-base">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              Start free trial
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-foreground underline underline-offset-4" data-testid="to-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div className="relative hidden lg:block">
        <img
          src="https://customer-assets-7cd3h4nn.emergentagent.net/job_quantum-housekeep/artifacts/9jttffmn_image.png"
          alt="Motel"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/70" />
        <div className="absolute bottom-0 left-0 right-0 p-12 text-primary-foreground">
          <p className="font-display text-3xl font-bold leading-tight">Run housekeeping like clockwork.</p>
          <p className="mt-3 max-w-md text-sm text-primary-foreground/80">
            Built to optimise and monitor motel cleaning — and adaptable to any task-oriented industry.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-primary-foreground/90">
            {["CSV import & daily task generation", "Cleaner mobile app with offline mode", "Timesheets & payroll export", "Actual vs estimated reporting"].map((f) => (
              <li key={f} className="flex items-center gap-2">
                <Check className="h-4 w-4" /> {f}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
